/**
 * Das Domaenenmodell von STARTS CashFlow.
 *
 * Kernidee: alles, was Geld bewegen *wird*, ist ein `PlannedItem` — egal ob es
 * aus einer MOCO-Rechnung, einer Fixkostenposition, einem Zahlungsplan, der
 * Umsatzsteuer-Vorausberechnung oder von Hand stammt. Alles, was Geld bereits
 * bewegt *hat*, ist eine `BankTransaction`. Ein `Match` verbindet beide Welten.
 *
 * Aus dieser Trennung folgt die zentrale Invariante des ganzen Systems:
 *
 *   Der Ist-Kontostand enthaelt bereits jede gebuchte Banktransaktion.
 *   Ein geplanter Posten fliesst deshalb nur noch mit seinem *offenen
 *   Restbetrag* in die Prognose ein. Ein Match realisiert den Plan —
 *   er addiert nichts hinzu.
 *
 * Solange diese Invariante gilt, ist Doppelzaehlung strukturell unmoeglich.
 */
/**
 * Wie viel eines Postens als bezahlt gilt.
 *
 * Es gibt zwei unabhaengige Quellen dafuer: was MOCO als Zahlung fuehrt, und
 * was hier gegen Bankbuchungen zugeordnet wurde. Sobald der Writeback aktiv
 * ist, kennen beide Systeme dieselbe Zahlung — sie zu addieren wuerde sie
 * doppelt zaehlen und den Posten faelschlich als erledigt markieren.
 *
 * Deshalb das Maximum, nicht die Summe. Und deshalb wird der Wert immer neu
 * aus beiden Quellen berechnet, statt bei jedem Ereignis erhoeht zu werden:
 * eine fortgeschriebene Zahl driftet, eine abgeleitete kann es nicht.
 */
export function resolveMatchedCents(item, confirmedMatchTotalCents) {
    return Math.min(Math.max(item.mocoPaidCents, confirmedMatchTotalCents), item.grossCents);
}
/** Der passende Status zu einem gedeckten Betrag. */
export function statusFor(item, matchedCents) {
    if (item.status === "cancelled")
        return "cancelled";
    // Eine spaeter doch gefundene Zahlung hebt die Annahme auf.
    if (item.status === "assumedSettled" && matchedCents === 0)
        return "assumedSettled";
    if (matchedCents >= item.grossCents)
        return "settled";
    return matchedCents > 0 ? "partiallyMatched" : "open";
}
/** Der noch nicht durch Bankbuchungen gedeckte Rest — die einzige Groesse, die in die Prognose darf. */
export function openCents(item) {
    if (item.status === "cancelled" || item.status === "settled" || item.status === "assumedSettled")
        return 0;
    const rest = item.grossCents - item.matchedCents;
    return rest > 0 ? rest : 0;
}
/** Vorzeichenbehafteter Effekt auf den Kontostand. */
export function signedOpenCents(item) {
    const open = openCents(item);
    return item.direction === "out" ? -open : open;
}
/** Noch nicht zugeordneter Teil einer Bankbuchung. */
export function unmatchedCents(tx) {
    if (tx.matchState === "ignored" || tx.matchState === "matched")
        return 0;
    const rest = Math.abs(tx.amountCents) - tx.matchedCents;
    return rest > 0 ? rest : 0;
}
/**
 * Ab dieser Konfidenz wird automatisch gematcht, darunter landet der Vorschlag
 * in der Abgleich-Inbox. Bewusst hoch angesetzt: ein falscher Auto-Match ist
 * teurer als ein manueller Klick.
 */
export const AUTO_MATCH_THRESHOLD = 90;
export const DEFAULT_SETTINGS = {
    vat: {
        // Vom Steuerbuero bestaetigt: Ist-Versteuerung mit Dauerfristverlaengerung.
        // Die Umsatzsteuer entsteht also erst mit dem Zahlungseingang, und die
        // Voranmeldung ist einen Monat spaeter faellig.
        scheme: "ist",
        period: "monthly",
        permanentExtension: true,
        // Basis fuer die Sondervorauszahlung (1/11). Solange 0, wird kein Posten
        // dafuer erzeugt — lieber keine Zahl als eine geratene.
        previousYearLiabilityCents: 0,
        taxOfficeIban: null,
    },
    bankHolidayState: null,
    /** 15.000 EUR als Startwert — in den Einstellungen jederzeit aenderbar. */
    minimumBalanceCents: 1_500_000,
    alertEmails: [],
    residualBufferEnabled: true,
    residualBufferLookbackMonths: 6,
    mocoWritebackAuto: false,
    applyPaymentBehaviour: true,
    updatedAt: "1970-01-01T00:00:00.000Z",
};
//# sourceMappingURL=types.js.map